/**
 * SystemBuilderUrl - URL state synchronization custom element
 *
 * Handles URL parsing, serialization, and history management for the system builder.
 * Listens for state change events and updates the URL accordingly.
 * Restores state from URL parameters on page load.
 *
 * URL format:
 * - ?subsystem={subSystemIndex} - Current subsystem
 * - &sub{X}layer{Y}group{Z}={variantId} - Product selection
 * - Example: ?subsystem=0&sub0layer0group0=123456&sub0layer0group1=789012
 */
class SystemBuilderUrl extends window.BAO.CustomElement() {
  #isLoadingFromURL = false

  /**
   * Called when the element is connected to the DOM
   */
  connectedCallback () {
    window.BAO.SystemBuilder.attach(this)
    super.connectedCallback()

    if (window.BAO.SystemBuilder.isReady) {
      this.loadFromURL()
    } else {
      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.SYSTEM_BUILDER.READY,
        () => this.loadFromURL(),
        { once: true },
      )
    }
  }

  /**
   * Set up event listeners for state changes
   */
  setupListeners () {
    super.setupListeners()

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.SYSTEM_BUILDER.SUBSYSTEM_CHANGED,
      this.#onStateChange.bind(this),
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.SYSTEM_BUILDER.PRODUCT_SELECTED,
      this.#onStateChange.bind(this),
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.SYSTEM_BUILDER.PRODUCT_REMOVED,
      this.#onStateChange.bind(this),
    )
  }

  /**
   * Load state from URL parameters on page load
   * Parses URLSearchParams and applies subsystem and product selections
   */
  loadFromURL () {
    this.#isLoadingFromURL = true

    const params = new URLSearchParams(window.location.search)

    const urlSubSystem = params.get('subsystem')
    if (urlSubSystem !== null) {
      const subSystemIndex = parseInt(urlSubSystem)
      if (!isNaN(subSystemIndex)) {
        window.BAO.eventBus.dispatchEvent(
          window.BAO.EVENTS.SYSTEM_BUILDER.SUBSYSTEM_CHANGED,
          { subSystemIndex },
        )
      }
    }

    const productSelections = []
    params.forEach((variantId, key) => {
      const match = key.match(/^sub(\d+)layer(\d+)group(\d+)$/)
      if (!match) return

      productSelections.push({
        subSystemIndex: parseInt(match[1]),
        layerIndex: parseInt(match[2]),
        groupIndex: parseInt(match[3]),
        variantId,
      })
    })

    if (productSelections.length === 0) {
      this.#isLoadingFromURL = false
      return
    }

    requestAnimationFrame(() => {
      const layersCache = new Map()

      productSelections.forEach(
        ({ subSystemIndex, layerIndex, groupIndex, variantId }) => {
          const cacheKey = `${subSystemIndex}-${layerIndex}`

          if (!layersCache.has(cacheKey)) {
            layersCache.set(
              cacheKey,
              window.BAO.SystemBuilder.getAllProductsForLayer(
                subSystemIndex,
                layerIndex,
              ),
            )
          }

          const allProducts = layersCache.get(cacheKey)

          allProducts.forEach((product) => {
            if (product.dataset.systemGroupIndex !== String(groupIndex)) return

            const select = product.querySelector(
              '[data-system-builder-el="select"]',
            )
            if (!select) return

            const optionIndex = Array.from(select.options).findIndex(
              (opt) => opt.value === variantId,
            )

            if (optionIndex === -1) return

            select.selectedIndex = optionIndex
            select.dispatchEvent(new Event('change', { bubbles: true }))

            const checkbox = product.querySelector(
              '[data-system-builder-el="checkbox"]',
            )
            if (checkbox) {
              checkbox.checked = true
              checkbox.dispatchEvent(new Event('change', { bubbles: true }))
            }
          })
        },
      )

      this.#isLoadingFromURL = false
    })
  }

  /**
   * Serialize current state to URL parameters
   * Updates browser history with current subsystem and product selections
   */
  handleURL () {
    const params = new URLSearchParams()

    const subSystem = this.state.data.subSystems[this.state.subSystemIndex]
    if (subSystem) {
      let hasSelections = false
      subSystem.layers.forEach((layer, layerIndex) => {
        layer.groups.forEach((group, groupIndex) => {
          if (group.productSelected?.variantId) {
            hasSelections = true
            params.set(
              `sub${this.state.subSystemIndex}layer${layerIndex}group${groupIndex}`,
              group.productSelected.variantId,
            )
          }
        })
      })

      if (hasSelections) {
        params.set('subsystem', this.state.subSystemIndex)
      }
    }

    const newURL = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname

    window.history.replaceState({}, '', newURL)
  }

  /**
   * Handle state change events
   * Updates URL when state changes occur
   *
   * @param {CustomEvent} event - State change event
   * @private
   */
  #onStateChange (event) {
    if (this.#isLoadingFromURL) return
    this.handleURL()
  }
}

window.customElements.define('system-builder-url', SystemBuilderUrl)
